import { authConfig } from '@/services/auth/auth-config';
import { clientEvents } from '@/services/telemetry/client-events';
import { sentryService } from '@/services/telemetry/sentry';
import { redactParsedUrl, redactUrlParams } from '@/shared/lib/redact-url';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';
import { ErrorParser, errorLogSummary, ParsedError } from './error-parser';

export interface RequestOptions {
  method?: string;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  body?: BodyInit;
  retryable?: boolean;
  silent?: boolean;
  [key: string]: unknown;
}

export type BinaryInput =
  | File
  | { blob: Blob; name: string }
  | Array<{ blob: Blob; name: string }>
  | null;

export type ResponseMiddleware<T = unknown> = (
  response: Response
) => Promise<T>;

// 120s accommodates large photo uploads over unreliable kiosk WiFi.
const REQUEST_TIMEOUT_MS = 120_000;

const RETRYABLE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
const RETRYABLE_STATUSES = new Set([502, 503, 504]);
const MAX_RETRY_ATTEMPTS = 8; // 1 original + 7 retries for GET/HEAD/OPTIONS
// Sized to ride out a backend Docker container restart: stop (2–10s) +
// start (5s) + healthcheck warmup (5–15s) = up to ~30s of nginx 502s.
// Backoff envelope with the 10s cap: 0.5+1+2+4+8+10+10 = ~35.5s — covers
// every observed restart window so retryable POSTs survive a deploy.
const MAX_POST_RETRY_ATTEMPTS = 8;

// Dev/test hook: `kiosk_developer_fastHttpRetries` localStorage flag compresses
// retry timings so flow tests don't wait through production's exponential
// backoff. Production timing is defensive for flaky real networks.
function resolveRetryDelays(): { baseMs: number; maxMs: number } {
  try {
    if (
      typeof localStorage !== 'undefined' &&
      localStorage.getItem('kiosk_developer_fastHttpRetries') === 'true'
    ) {
      return { baseMs: 5, maxMs: 10 };
    }
  } catch {
    // localStorage may throw in sandboxed envs
  }
  return { baseMs: 500, maxMs: 10_000 };
}

// Ceiling on offline pause per retry. Tighter than a typical WiFi
// reconnect cycle (10–30s) on purpose: a kiosk operator staring at
// "Printing…" gets impatient fast. 8s × 4 retry sleeps = ~32s of
// offline waiting in the worst case, which is the upper bound of
// human-acceptable kiosk latency.
//
// Caveat: `navigator.onLine` is reliable on Mac/Windows WiFi but
// stays `true` on Linux+Ethernet even when upstream is unreachable.
// Mini-PC kiosks (most of our fleet) are on Ethernet, so this code
// path is mainly a no-op for them — the 5-attempt bump above is
// what helps. This pause primarily benefits VIRTUAL_KIOSK (mobile
// hotspot) where the OS surfaces real WiFi state.
const ONLINE_WAIT_MAX_MS = 8_000;

/**
 * Wait until `navigator.onLine` is true, or `maxWaitMs` elapses, or the
 * caller aborts. Returns immediately if already online.
 *
 * Used in place of (NOT in addition to) the exp-backoff sleep when the
 * browser reports offline. Stacking with backoff would push the
 * worst-case retry-loop latency to >100s, which is unacceptable on a
 * kiosk where the operator restarts the app at ~30s of perceived hang.
 */
async function waitForOnline(
  maxWaitMs: number,
  signal?: AbortSignal
): Promise<void> {
  if (!isBrowserOffline()) return;
  if (signal?.aborted) return;

  await new Promise<void>((resolve) => {
    const cleanup = () => {
      window.removeEventListener('online', onOnline);
      signal?.removeEventListener('abort', onAbort);
      clearTimeout(timer);
    };
    const onOnline = () => {
      cleanup();
      resolve();
    };
    const onAbort = () => {
      cleanup();
      resolve();
    };
    const timer = setTimeout(() => {
      cleanup();
      resolve();
    }, maxWaitMs);
    window.addEventListener('online', onOnline, { once: true });
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

const { baseMs: RETRY_BASE_DELAY_MS, maxMs: RETRY_MAX_DELAY_MS } =
  resolveRetryDelays();

// Connection-state hooks. App wires these to a Pinia store at boot so the
// HTTP layer stays Pinia-free (callable from non-Vue contexts and unit
// tests). Both default to no-ops; if the app never wires them the retry
// loop still works, the banner just never shows.
const noopConnectionHook = (): void => undefined;
let onRetryStarted: () => void = noopConnectionHook;
let onRequestSettled: () => void = noopConnectionHook;

export function setConnectionStateHooks(hooks: {
  onRetryStarted?: () => void;
  onRequestSettled?: () => void;
}): void {
  if (hooks.onRetryStarted) {
    onRetryStarted = hooks.onRetryStarted;
  }
  if (hooks.onRequestSettled) {
    onRequestSettled = hooks.onRequestSettled;
  }
}

function safelyRunConnectionHook(hook: () => void): void {
  try {
    hook();
  } catch {
    // UI connection-state hooks must never fail the HTTP request itself.
  }
}

function sanitizeUrl(url: string | null | undefined): string {
  if (!url) return String(url);
  return redactUrlParams(url);
}

function sanitizeUrlForLogging(url: unknown): string | undefined {
  if (!url || typeof url !== 'string') return url as string | undefined;
  try {
    return redactParsedUrl(
      new URL(url, window.location.origin),
      url.startsWith('/')
    );
  } catch {
    return sanitizeUrl(url);
  }
}

function createFormData(
  data: Record<string, unknown> | undefined,
  binaries: Record<string, BinaryInput> | undefined
): FormData {
  const formData = new FormData();

  if (data) {
    for (const key in data) {
      if (Object.hasOwn(data, key)) {
        const value = data[key];
        // Skip null/undefined — FormData.append stringifies them to "null"/"undefined"
        if (value === null || value === undefined) continue;
        formData.append(key, value as string | Blob);
      }
    }
  }

  if (binaries) {
    for (const key in binaries) {
      if (!Object.hasOwn(binaries, key)) continue;
      const binary = binaries[key];
      if (binary == null) {
        console.warn(
          `[Http] createFormData: skipping null/undefined binary for key "${key}"`
        );
        continue;
      }

      if (typeof File !== 'undefined' && binary instanceof File) {
        formData.append(key, binary);
      } else if (Array.isArray(binary)) {
        for (const item of binary) {
          if (item?.blob) formData.append(key, item.blob, item.name);
        }
      } else if ('blob' in binary && binary.blob) {
        formData.append(key, binary.blob, binary.name);
      } else {
        console.warn(
          `[Http] createFormData: binary entry "${key}" has no blob property, skipping`
        );
      }
    }
  }

  return formData;
}

function parseHttpRequestArguments(
  methodOrRequestParams: string | RequestOptions | undefined,
  data: unknown,
  binaries: Record<string, BinaryInput> | undefined
): RequestOptions {
  let method: string;
  let requestParams: RequestOptions;

  if (
    typeof methodOrRequestParams === 'object' &&
    methodOrRequestParams !== null
  ) {
    method = methodOrRequestParams.method ?? 'GET';
    requestParams = { ...methodOrRequestParams, method };
  } else if (typeof methodOrRequestParams === 'string') {
    method = methodOrRequestParams;
    requestParams = { method };
  } else {
    // Default to GET when method is undefined/null/non-string
    method = 'GET';
    requestParams = { method };
  }

  if (method === 'POST' || method === 'PATCH' || method === 'PUT') {
    if (binaries) {
      requestParams.body = createFormData(
        data as Record<string, unknown>,
        binaries
      );
    } else {
      requestParams.body = JSON.stringify(data);
      requestParams.headers = {
        ...requestParams.headers,
        'Content-Type': 'application/json',
      };
    }
  }

  return requestParams;
}

function applyMiddleware<T>(
  response: Response,
  middleware?: ResponseMiddleware<T>
): Promise<T> {
  if (typeof middleware === 'function') return middleware(response);
  return response.json() as Promise<T>;
}

// Prevents memory/connection leaks when the body was only partially read
// (e.g., response.json() failed on malformed JSON). An unconsumed ReadableStream
// holds a reference to the TCP connection and leaks memory on 24/7 operation.
function drainResponseBody(response: Response): void {
  try {
    if (response.bodyUsed) return;
    if (response.body && typeof response.body.cancel === 'function') {
      response.body.cancel().catch(() => {
        // Ignore — body may already be disturbed or locked
      });
    } else {
      // Fallback for polyfill environments
      response.text().catch(() => {});
    }
  } catch {
    // Defensive — some environments throw on .body after partial read
  }
}

export function httpStatusOf(error: unknown): number | undefined {
  const e = error as {
    context?: {
      response?: { status?: { code?: number } };
    };
  };
  return e?.context?.response?.status?.code;
}

const NETWORK_FAILURE_MESSAGES = [
  'Failed to fetch',
  'Load failed',
  'NetworkError',
  'net::ERR_',
];

function isBrowserOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

// ErrorParser wraps the original error, so its name survives only on `cause`.
function errorNames(error: unknown): string[] {
  const e = error as (Error & { cause?: unknown }) | undefined;
  return [e?.name, (e?.cause as Error | undefined)?.name].filter(
    (n): n is string => typeof n === 'string'
  );
}

/**
 * True when the request never got an HTTP answer: the socket failed, the
 * browser reports offline, or the request was aborted / timed out. These
 * self-heal (retry loop, queued analytics, keepalive beacon) and carry no
 * server-side signal, so callers use this to decide between "retry / warn"
 * and "capture". An error that carries an HTTP status is never one.
 */
export function isNetworkFailure(error: unknown): boolean {
  if (httpStatusOf(error) !== undefined) return false;
  if (
    errorNames(error).some((n) => n === 'AbortError' || n === 'TimeoutError')
  ) {
    return true;
  }
  if (isBrowserOffline()) return true;
  const msg = (error as Error | undefined)?.message ?? '';
  return NETWORK_FAILURE_MESSAGES.some((needle) => msg.includes(needle));
}

function isTransientError(error: unknown): boolean {
  // Never retry timeouts — the caller set a deliberate time limit
  if (errorNames(error).includes('TimeoutError')) return false;

  const statusCode = httpStatusOf(error);
  if (statusCode && RETRYABLE_STATUSES.has(statusCode)) return true;

  return isNetworkFailure(error);
}

// Always resolves (never rejects) to keep retry loop control simple.
function abortableDelay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise<void>((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true }
    );
  });
}

interface FetchErrorContext {
  url: string;
  requestParams: RequestOptions;
  data: unknown;
  binaries: Record<string, BinaryInput> | undefined;
  silent: boolean;
  callerSignal: AbortSignal | undefined;
  timeoutController: AbortController;
  effectiveTimeout: number;
}

async function handleFetchError(
  fetchError: Error,
  ctx: FetchErrorContext
): Promise<never> {
  const {
    url,
    requestParams,
    data,
    binaries,
    silent,
    callerSignal,
    timeoutController,
    effectiveTimeout,
  } = ctx;
  const method = requestParams.method ?? 'GET';
  const sanitizedUrl = sanitizeUrl(url);

  // Distinguish caller abort vs timeout vs genuine network error. Caller abort
  // (e.g. session reset) happens 5-10 times per reset — skip Sentry noise.
  const isCallerAbort =
    fetchError.name === 'AbortError' && Boolean(callerSignal?.aborted);
  if (isCallerAbort) {
    throw new ParsedError(`Request aborted: ${method} ${sanitizedUrl}`, {
      cause: fetchError,
      context: {
        request: { method, url: sanitizedUrl },
        isRequestManuallyAborted: true,
      },
    });
  }

  // Check timeoutController directly rather than absence of callerSignal —
  // avoids race when both fire in the same tick.
  const isTimeout =
    fetchError.name === 'AbortError' &&
    timeoutController.signal.aborted &&
    !callerSignal?.aborted;

  const enrichedError = isTimeout
    ? Object.assign(
        new Error(
          `Request timeout after ${effectiveTimeout}ms: ${method} ${sanitizedUrl}`
        ),
        { name: 'TimeoutError' }
      )
    : fetchError;

  if (!silent) {
    sentryService.addBreadcrumb(
      'fetch',
      `Network error: ${method} ${sanitizedUrl} - ${enrichedError.name}: ${enrichedError.message}`,
      { url: sanitizedUrl, method },
      'error',
      'http'
    );
  }

  // If ErrorParser itself throws, fall back to a minimal ParsedError so the
  // original network/timeout error context is not lost.
  let errorWithContext: ParsedError;
  try {
    errorWithContext = await ErrorParser.generateErrorWithContext({
      error: enrichedError,
      url,
      method,
      data,
      binaries: binaries as Record<string, unknown> | undefined,
      messagePrefix: `API error in ${sanitizedUrl}`,
      isRequestManuallyAborted: false,
    });
  } catch (parserError) {
    console.warn(
      '[HTTP] ErrorParser.generateErrorWithContext failed:',
      (parserError as Error)?.message
    );
    errorWithContext = new ParsedError(
      `API error in ${sanitizedUrl}: ${enrichedError.message || 'unknown error'}`,
      {
        cause: enrichedError,
        context: { request: { method, url: sanitizedUrl } },
      }
    );
  }

  // The error holds the request body, which can hold the traits of the
  // people. The log holds the method, the path and the codes only.
  console.error('[HTTP] Request failed', errorLogSummary(errorWithContext));
  throw errorWithContext;
}

async function makeRequestOnce<T>(
  url: string,
  methodOrRequestParams: string | RequestOptions | undefined,
  data: unknown,
  binaries: Record<string, BinaryInput> | undefined,
  middleware: ResponseMiddleware<T> | undefined
): Promise<T> {
  // Validate URL early — fetch() with null/undefined stringifies them to
  // "null"/"undefined" which resolves to a relative path, causing misleading 404s.
  if (!url || typeof url !== 'string') {
    throw new ParsedError(
      `Http.makeRequest called with invalid URL: ${String(url)}`,
      {
        context: { request: { url: String(url) } },
      }
    );
  }

  const silent =
    typeof methodOrRequestParams === 'object'
      ? Boolean(methodOrRequestParams?.silent)
      : false;

  // Strip silent/retryable before passing to fetch — they aren't valid fetch options
  let effectiveParams: string | RequestOptions | undefined =
    methodOrRequestParams;
  if (
    typeof methodOrRequestParams === 'object' &&
    methodOrRequestParams !== null
  ) {
    const { silent: _s, retryable: _r, ...cleanParams } = methodOrRequestParams;
    if (_s !== undefined || _r !== undefined) {
      effectiveParams = cleanParams;
    }
  }

  const requestParams = parseHttpRequestArguments(
    effectiveParams,
    data,
    binaries
  );

  // Auth headers. Backend's ``require_kiosk_auth`` derives the kiosk
  // scope from the api key alone (api keys are kiosk-scoped at issue
  // time); ``X-Kiosk-Id`` is not consumed anywhere on the backend.
  const apiKey = authConfig.getApiKey();
  if (apiKey) {
    requestParams.headers = {
      ...requestParams.headers,
      'X-Kiosk-Api-Key': apiKey,
    };
  }

  // Sentry distributed tracing headers
  const sentryHeaders = sentryService.getTraceHeaders();
  if (Object.keys(sentryHeaders).length > 0) {
    requestParams.headers = { ...requestParams.headers, ...sentryHeaders };
  }

  // Timeout AbortController actually cancels the fetch. Without it, Promise.race
  // only ignores the response — the TCP connection stays open, response body is
  // never consumed (memory leak), stalled requests accumulate on 24/7 kiosks.
  const effectiveTimeout = REQUEST_TIMEOUT_MS;
  const timeoutController = new AbortController();
  const timeoutId = setTimeout(
    () => timeoutController.abort(),
    effectiveTimeout
  );

  // Compose timeout with caller's signal. Named listener so we can remove it
  // after completion — without explicit removal, the listener closure (capturing
  // timeoutController) leaks on callerSignal for every completed request.
  const callerSignal = requestParams.signal;
  let callerAbortHandler: (() => void) | null = null;
  if (callerSignal) {
    if (callerSignal.aborted) {
      clearTimeout(timeoutId);
      timeoutController.abort();
    } else {
      callerAbortHandler = () => timeoutController.abort();
      callerSignal.addEventListener('abort', callerAbortHandler, {
        once: true,
      });
    }
  }
  requestParams.signal = timeoutController.signal;

  const cleanupCallerListener = () => {
    if (callerAbortHandler && callerSignal) {
      callerSignal.removeEventListener('abort', callerAbortHandler);
      callerAbortHandler = null;
    }
  };

  let response: Response;
  try {
    response = await fetch(url, requestParams as RequestInit);
  } catch (fetchError) {
    clearTimeout(timeoutId);
    cleanupCallerListener();
    return handleFetchError(fetchError as Error, {
      url,
      requestParams,
      data,
      binaries,
      silent,
      callerSignal,
      timeoutController,
      effectiveTimeout,
    });
  }

  // Fetch succeeded (got headers) — clear timeout only after body consumption.
  // If the server streams the body slowly, the timeout must still fire to
  // prevent hanging indefinitely on response.text()/json() downstream.
  if (!response.ok) {
    try {
      // ErrorParser.generateErrorWithContext adds its own breadcrumb — don't
      // add one here to avoid duplicates.
      throw await ErrorParser.generateErrorWithContext({ response });
    } catch (errorPathError) {
      // If caller aborted DURING body consumption (response.text() inside
      // ErrorParser), we get a raw AbortError. Re-classify so callers see
      // isRequestManuallyAborted instead.
      const e = errorPathError as Error;
      if (
        !(errorPathError instanceof ParsedError) &&
        e?.name === 'AbortError' &&
        callerSignal?.aborted
      ) {
        throw new ParsedError(
          `Request aborted during error parsing: ${requestParams.method} ${sanitizeUrl(url)}`,
          {
            cause: e,
            context: {
              request: { method: requestParams.method, url: sanitizeUrl(url) },
              response: {
                status: { code: response.status, text: response.statusText },
              },
              isRequestManuallyAborted: true,
            },
          }
        );
      }
      throw errorPathError;
    } finally {
      clearTimeout(timeoutId);
      cleanupCallerListener();
    }
  }

  // Success breadcrumb (100% sampling). Skip entirely for silent requests
  // (polling endpoints) to reduce Sentry noise.
  if (!silent) {
    const sanitizedUrl = sanitizeUrlForLogging(url);
    const method = requestParams.method ?? 'GET';
    sentryService.addBreadcrumb(
      'fetch',
      `${method} ${sanitizedUrl}`,
      { url: sanitizedUrl, method, status_code: response.status },
      'info',
      'http'
    );
  }

  try {
    return await applyMiddleware(response, middleware);
  } catch (parseError) {
    const e = parseError as Error;
    // Caller aborted during body consumption — reclassify
    if (e?.name === 'AbortError' && callerSignal?.aborted) {
      drainResponseBody(response);
      throw new ParsedError(
        `Request aborted during response parsing: ${requestParams.method} ${sanitizeUrl(url)}`,
        {
          cause: e,
          context: {
            request: { method: requestParams.method, url: sanitizeUrl(url) },
            response: {
              status: { code: response.status, text: response.statusText },
            },
            isRequestManuallyAborted: true,
          },
        }
      );
    }
    // Timeout during body consumption
    if (e?.name === 'AbortError' && timeoutController.signal.aborted) {
      drainResponseBody(response);
      const timeoutError = new ParsedError(
        `Request timeout during response parsing after ${effectiveTimeout}ms: ${requestParams.method} ${sanitizeUrl(url)}`,
        {
          cause: e,
          context: {
            request: { method: requestParams.method, url: sanitizeUrl(url) },
            response: {
              status: { code: response.status, text: response.statusText },
            },
          },
        }
      );
      timeoutError.name = 'TimeoutError';
      throw timeoutError;
    }
    // Middleware failed — drain body so browser releases TCP connection
    drainResponseBody(response);
    throw parseError;
  } finally {
    clearTimeout(timeoutId);
    cleanupCallerListener();
  }
}

async function makeRequest<T = unknown>(
  url: string,
  methodOrRequestParams?: string | RequestOptions,
  data?: unknown,
  binaries?: Record<string, BinaryInput>,
  middleware?: ResponseMiddleware<T>
): Promise<T> {
  const method =
    (typeof methodOrRequestParams === 'object'
      ? methodOrRequestParams?.method
      : methodOrRequestParams) ?? 'GET';

  const isIdempotentMethod = RETRYABLE_METHODS.has(method.toUpperCase());
  const isRetryablePost =
    !isIdempotentMethod &&
    typeof methodOrRequestParams === 'object' &&
    methodOrRequestParams?.retryable === true;
  const isRetryable = isIdempotentMethod || isRetryablePost;
  const maxAttempts = isRetryable
    ? isIdempotentMethod
      ? MAX_RETRY_ATTEMPTS
      : MAX_POST_RETRY_ATTEMPTS
    : 1;

  const callerSignal =
    typeof methodOrRequestParams === 'object'
      ? methodOrRequestParams?.signal
      : undefined;

  let lastError: unknown;
  let retriesAttempted = 0;
  let reconnectingStarted = false;
  const startReconnecting = (): void => {
    if (reconnectingStarted) {
      return;
    }
    reconnectingStarted = true;
    safelyRunConnectionHook(onRetryStarted);
  };
  const finishReconnecting = (): void => {
    if (!reconnectingStarted) {
      return;
    }
    reconnectingStarted = false;
    safelyRunConnectionHook(onRequestSettled);
  };

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const result = await makeRequestOnce<T>(
        url,
        methodOrRequestParams,
        data,
        binaries,
        middleware
      );
      // Success on a retry: clear reconnecting state so the banner hides.
      // Add a Sentry breadcrumb tagging the recovery so deploy-window 502s
      // are distinguishable from persistent outages in event search.
      if (retriesAttempted > 0) {
        try {
          sentryService.addBreadcrumb(
            'fetch',
            `Recovered after ${retriesAttempted} retr${retriesAttempted === 1 ? 'y' : 'ies'}: ${method} ${sanitizeUrlForLogging(url)}`,
            {
              url: sanitizeUrlForLogging(url),
              method,
              retries_attempted: retriesAttempted,
              connection_recovered_after_retry: true,
            },
            'info',
            'http'
          );
        } catch {
          // Sentry call failures are non-fatal
        }
      }
      finishReconnecting();
      return result;
    } catch (error) {
      lastError = error;
      // Never retry manually aborted requests
      const ctx = (error as ParsedError)?.context as
        | (Record<string, unknown> & { isRequestManuallyAborted?: boolean })
        | undefined;
      if (ctx?.isRequestManuallyAborted) {
        finishReconnecting();
        throw error;
      }
      if (callerSignal?.aborted) {
        finishReconnecting();
        throw error;
      }
      if (attempt >= maxAttempts || !isTransientError(error)) {
        // Stamp the final error so callers (captureApiError) can tag
        // recovered/persistent failures distinctly.
        if (error && typeof error === 'object' && ctx) {
          try {
            ctx['retriesAttempted'] = retriesAttempted;
          } catch {
            // ctx frozen — non-fatal
          }
        }
        finishReconnecting();
        throw error;
      }
      retriesAttempted = attempt;
      startReconnecting();

      const sanitizedUrl = sanitizeUrl(url);
      const isOffline = isBrowserOffline();
      console.warn(
        `[HTTP] Transient error (${httpStatusOf(error) ?? 'network'}), ` +
          `retry ${attempt}/${maxAttempts - 1}: ${method} ${sanitizedUrl}` +
          (isOffline ? ' [offline — waiting for network]' : '')
      );
      sentryService.addBreadcrumb(
        'fetch',
        `Retry ${attempt}/${maxAttempts - 1}: ${method} ${sanitizedUrl}`,
        {
          url: sanitizeUrlForLogging(url),
          method,
          ...(isOffline && { offline_pause: true }),
        },
        'warning',
        'http'
      );
      clientEvents.record(
        'api_retry',
        {
          method,
          endpoint: sanitizeUrlForLogging(url),
          attempt,
          max_attempts: maxAttempts,
          status_code: httpStatusOf(error),
          offline_pause: isOffline,
        },
        { area: 'api', level: 'warning' }
      );

      // Pause between attempts. When offline, replace the exp-backoff
      // with a "wait for online" pause (capped at ONLINE_WAIT_MAX_MS) —
      // they don't STACK. Stacking would push worst-case latency to
      // 30s + 5s per failed attempt × 4 = ~140s, well past the kiosk
      // operator's patience threshold. Either path is bounded; the
      // online-wait is the cleaner shape for a dead-network scenario
      // (no point sleeping the backoff and ALSO waiting for online).
      if (isOffline) {
        await waitForOnline(ONLINE_WAIT_MAX_MS, callerSignal);
      } else {
        const delay = Math.min(
          RETRY_BASE_DELAY_MS * 2 ** (attempt - 1),
          RETRY_MAX_DELAY_MS
        );
        await abortableDelay(delay, callerSignal);
      }

      if (callerSignal?.aborted) {
        finishReconnecting();
        throw error;
      }
    }
  }
  // Unreachable (loop either returns or throws), but TS doesn't know that.
  finishReconnecting();
  throw lastError;
}

export interface KeepaliveOptions {
  method?: 'POST' | 'PATCH';
  /** The customer routes authenticate by download code, so they pass null. */
  apiKey: string | null;
  /** At session end the store holds a cleared id, so the caller passes it. */
  sessionId?: string | null;
}

/**
 * A request that outlives the page: `fetch` with `keepalive`, outside the
 * abort registry, so neither abortAll() nor the page teardown stops it.
 * sendBeacon cannot set the api key header. Resolves to null when the
 * request does not complete.
 */
export function sendKeepalive(
  path: string,
  body: unknown,
  { method = 'POST', apiKey, sessionId = null }: KeepaliveOptions
): Promise<Response | null> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Kiosk-Trace-ID': clientEvents.getTraceId(),
    ...sentryService.getTraceHeaders(),
  };
  if (apiKey) {
    headers['X-Kiosk-Api-Key'] = apiKey;
  }
  if (sessionId) {
    headers['X-Session-ID'] = sessionId;
  }
  return fetch(`${window.location.origin}${path}`, {
    method,
    keepalive: true,
    headers,
    body: JSON.stringify(body),
  }).catch(() => null);
}

// biome-ignore lint/complexity/noStaticOnlyClass: namespace grouping for backward compat with old Http API
export class Http {
  static REQUEST_TIMEOUT_MS = REQUEST_TIMEOUT_MS;

  static sanitizeUrlForLogging = sanitizeUrlForLogging;
  static makeRequest = makeRequest;
  static setConnectionStateHooks = setConnectionStateHooks;
}

exposeToGlobalScope(Http, 'Http');
